import React, { useEffect, useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import { getLocations } from '../../services/inventoryCorrections.service';
import { getDetailReasons } from '../../services/reason.service';
import { countAction, CountsOverview, getCountsOverview } from '../../services/count.service';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { useResponsive } from '../../hook/useResponsive';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import {
  Button,
  ConfirmDialog,
  COUNT_STATUS,
  COUNT_STATUS_GROUPS,
  DocListRow,
  DocumentTable,
  EmptyState,
  ErrorBanner,
  FilterDef,
  FiltersSheet,
  ListState,
  MenuItem,
  PageFrame,
  Pager,
  PageTitle,
  PhoneDocumentList,
  StatusStrip,
  ToolbarCard,
  countStatusOf,
  dayMonth,
  periodTextOf,
  rangeOf,
  readListState,
  useLanguage,
  useOverviewData,
  writeListState,
} from '../../components/purchasing';
import NewCountDialog, { COUNT_KINDS } from './NewCountDialog';

const PAGE_SIZE = 20;
const STATUS_KEYS = COUNT_STATUS_GROUPS.map((group) => group.key);
const COUNTING: number[] = [COUNT_STATUS.Draft, COUNT_STATUS.RecountRequested];

/** Stock counts of the company's sites: the header site's by default. */
const Count = () => {
  const preLanguage = localStorage.getItem('language');
  const userId = localStorage.getItem('userId') || '';
  const companyId = localStorage.getItem('companyId') || '';
  const headerSite = localStorage.getItem('locationId') || '';
  const { isMobile: phone } = useResponsive();
  const { language, t } = useLanguage();

  const [state, setState] = useState<ListState>(() => readListState(STATUS_KEYS, ['kind']));
  const [term, setTerm] = useState(state.search.trim());
  const [sites, setSites] = useState<{ id: string; name: string }[]>([]);
  const [reasons, setReasons] = useState<{ id: string; name: string }[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // /count/new lands here with the dialog open.
  const [creating, setCreating] = useState(() => /(^|[?&])new=1(&|$)/.test(location.search));
  const [cancelling, setCancelling] = useState<DocListRow | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);

  const update = (patch: Partial<ListState>) => setState((prev) => ({ ...prev, page: 1, ...patch }));
  const setFilter = (key: string, value: string) => setState((prev) => ({ ...prev, page: 1, filters: { ...prev.filters, [key]: value } }));

  useEffect(() => writeListState(state), [state]);
  useDebounceEffect(() => setTerm(state.search.trim()), [state.search], 300);

  useEffect(() => {
    const load = () => {
      getLocations(userId, companyId, 0, 0, '')
        .then((response: any) => setSites((response?.data?.data ?? []).map((row: any) => ({ id: String(row.id), name: String(row.name ?? '') }))))
        .catch(() => undefined);
      getDetailReasons(companyId, '2')
        .then((response: any) => setReasons((response?.data?.data ?? []).map((row: any) => ({ id: String(row.id), name: String(row.reasonName ?? '') }))))
        .catch(() => undefined);
    };
    load();
    document.addEventListener('newToken', load);
    return () => document.removeEventListener('newToken', load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const kind = COUNT_KINDS.some((entry) => String(entry.type) === state.filters.kind) ? state.filters.kind : '';
  const { data, loading, failed, reload } = useOverviewData<CountsOverview>(
    () =>
      getCountsOverview({
        ...rangeOf(state),
        status: state.status === 'all' ? undefined : state.status,
        type: kind || undefined,
        search: term || undefined,
        locationId: state.site || undefined,
        page: state.page,
        limit: PAGE_SIZE,
      }),
    [state.period, state.from, state.to, state.status, state.site, kind, state.page, term],
    language,
  );

  const siteName = sites.find((site) => site.id === state.site)?.name;
  const periodText = periodTextOf(state, t);
  const countsText = (count: number) => (count === 1 ? t('1 count') : t('{count} counts', { count }));
  const kindText = (type: number) => t(COUNT_KINDS.find((entry) => entry.type === type)?.label ?? 'All products');
  const meta = [phone ? null : state.site ? siteName : t('All sites'), periodText, data ? countsText(Number(data.statusCounts?.all ?? 0)) : null].filter(Boolean).join(' · ');

  const counts = data?.statusCounts ?? {};
  const strip = COUNT_STATUS_GROUPS.filter((group) => group.key !== 'cancelled' || (counts.cancelled ?? 0) > 0 || state.status === 'cancelled').map((group) => ({
    key: group.key,
    label: t(group.label),
    family: group.family,
    count: Number(counts[group.key] ?? 0),
  }));

  const statusIds = new Map((data?.rows ?? []).map((row) => [row.id, row.statusId]));
  const showSite = !state.site;
  const rows: DocListRow[] = (data?.rows ?? []).map((row) => {
    const created = dayMonth(row.createdAt);
    const person = row.createdBy?.name ?? null;
    const site = row.location?.name ?? '—';
    const counted = t('{counted} of {lines}', { counted: row.counted, lines: row.lines });
    const decided = row.statusId === COUNT_STATUS.Completed || row.statusId === COUNT_STATUS.Rejected ? row.approvedBy?.name ?? null : null;
    return {
      id: row.id,
      number: `#${row.internalNumber}`,
      subtitle: row.reason?.name ?? null,
      cells: {
        site: { main: site, sub: kindText(row.type) },
        created: { main: created, sub: person },
        counted: { main: counted },
      },
      status: countStatusOf(row.statusId),
      statusNote: decided,
      phoneText: [showSite ? site : null, kindText(row.type)].filter(Boolean).join(' · '),
      phoneMeta: [created, person, t('{counted} of {lines} counted', { counted: row.counted, lines: row.lines })].filter(Boolean).join(' · '),
    };
  });

  const hrefOf = (row: DocListRow) => `/${preLanguage}/count/${row.id}`;
  const open = (row: DocListRow) => webNavigate(hrefOf(row));
  const menuFor = (row: DocListRow): MenuItem[] => [
    { key: 'open', label: t('Open count'), icon: 'arrowRight', onSelect: () => open(row) },
    ...(COUNTING.includes(Number(statusIds.get(row.id))) ? [{ key: 'cancel', label: t('Cancel count'), icon: 'close' as const, danger: true, onSelect: () => setCancelling(row) }] : []),
  ];

  const confirmCancel = async () => {
    if (!cancelling) return;
    setCancelBusy(true);
    try {
      await countAction(cancelling.id, 'cancel');
      showToast({ message: t('Count cancelled'), type: 'success', duration: 5000 });
      setCancelling(null);
      reload();
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setCancelBusy(false);
    }
  };

  const filters: FilterDef[] = [
    { key: 'site', label: 'Site', icon: 'pin', allLabel: 'All sites', value: state.site, options: sites, onChange: (value) => update({ site: value }) },
    {
      key: 'kind',
      label: 'What is counted',
      icon: 'box',
      allLabel: 'Every kind of count',
      value: kind,
      options: COUNT_KINDS.map((entry) => ({ id: String(entry.type), name: t(entry.label) })),
      onChange: (value) => setFilter('kind', value),
    },
  ];

  const newCount = () => setCreating(true);
  const statusLabel = COUNT_STATUS_GROUPS.find((group) => group.key === state.status)?.label;
  const empty = term ? (
    <EmptyState icon="search" phone={phone} title={t('No counts match "{search}"', { search: term })} desc={t('Check the number, or search by product name or SKU.')} />
  ) : state.status !== 'all' && statusLabel ? (
    <EmptyState phone={phone} title={t('No counts with this status')} desc={t('{status} counts in this period show here.', { status: t(statusLabel) })} />
  ) : (
    <EmptyState
      phone={phone}
      title={t('No counts in this period')}
      desc={t('Count what is on the shelf; approving the count corrects the stock.')}
      action={
        <Button variant="primary" phone={phone} icon="plus" onClick={newCount}>
          {t('New count')}
        </Button>
      }
    />
  );

  const pager = data ? <Pager phone={phone} t={t} page={state.page} limit={PAGE_SIZE} total={data.count} onPage={(page) => setState((prev) => ({ ...prev, page }))} /> : null;

  return (
    <PageFrame phone={phone}>
      <PageTitle phone={phone} t={t} title={t('Stock counts')} meta={meta} primary={{ key: 'new', label: t('New count'), icon: 'plus', onSelect: newCount }} />
      <ToolbarCard
        phone={phone}
        t={t}
        period={state.period}
        onPeriod={(period) => update({ period })}
        customFrom={state.from}
        customTo={state.to}
        onCustom={(from, to) => update({ from, to })}
        search={state.search}
        onSearch={(search) => setState((prev) => ({ ...prev, search, page: 1 }))}
        searchLabel={t('Search counts')}
        searchPlaceholder={phone ? t('Search counts') : t('Number or product')}
        filters={filters}
        onOpenFilters={() => setFiltersOpen(true)}
      />
      <StatusStrip phone={phone} label={t('Count status')} items={strip} active={state.status} onPick={(status) => update({ status })} />
      {failed && <ErrorBanner message={t('The counts could not be loaded.')} retryLabel={t('Try again')} onRetry={reload} />}
      {phone ? (
        <PhoneDocumentList t={t} label={t('Stock counts')} rows={rows} loading={loading} hrefOf={hrefOf} onOpen={open} empty={empty} footer={pager} />
      ) : (
        <DocumentTable
          t={t}
          label={t('Stock counts')}
          numberLabel={t('Count')}
          columns={[
            { key: 'site', label: t('Site') },
            { key: 'created', label: t('Created'), compact: true },
            { key: 'counted', label: t('Counted'), align: 'right', compact: true },
          ]}
          rows={rows}
          loading={loading}
          hrefOf={hrefOf}
          onOpen={open}
          menuFor={menuFor}
          menuLabel={(row) => t('Actions for count {number}', { number: row.number })}
          triggerLabel={(row) => t('More actions for count {number}', { number: row.number })}
          empty={empty}
          footer={pager}
        />
      )}
      {phone && (
        <FiltersSheet
          open={filtersOpen}
          t={t}
          filters={filters}
          showLabel={data?.count === 1 ? t('Show 1 count') : t('Show {count} counts', { count: data?.count ?? 0 })}
          onClear={() => setState((prev) => ({ ...prev, page: 1, site: '', filters: { ...prev.filters, kind: '' } }))}
          onClose={() => setFiltersOpen(false)}
        />
      )}
      <NewCountDialog
        open={creating}
        phone={phone}
        t={t}
        language={language}
        sites={sites}
        reasons={reasons}
        defaultSiteId={state.site || headerSite}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          webNavigate(`/${preLanguage}/count/${id}`);
        }}
      />
      <ConfirmDialog
        open={!!cancelling}
        phone={phone}
        danger
        busy={cancelBusy}
        title={t('Cancel count {number}?', { number: cancelling?.number ?? '' })}
        body={t('The count is marked cancelled and no stock changes. This cannot be undone.')}
        confirmLabel={t('Cancel count')}
        cancelLabel={t('Keep count')}
        closeLabel={t('Close')}
        onConfirm={confirmCancel}
        onCancel={() => setCancelling(null)}
      />
    </PageFrame>
  );
};

export default Count;
